import * as Application from 'expo-application';
import Constants from 'expo-constants';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { ScrollView, StyleSheet, View } from 'react-native';
import { Appbar, Text } from 'react-native-paper';

import { ScreenAppbar } from '@/components/atoms';
import {
  ProfileCard,
  SettingsAppInfoSection,
  SettingsMenuList,
  SignOutCard,
} from '@/components/organisms';
import { NetworkSettings } from '@/components/pages/settings/NetworkSettings';
import { QuotaUsageSettings } from '@/components/pages/settings/QuotaUsageSettings';
import { StorageSettings } from '@/components/pages/settings/StorageSettings';
import { useTranslation } from '@/i18n';
import { useAuthStore } from '@/stores';
import { spacing, unisonColors, useAppTheme } from '@/theme';

const NETWORK_VIEW = 'network';
const QUOTA_USAGE_VIEW = 'quotaUsage';
const STORAGE_VIEW = 'storage';

const SettingsMenuView = () => {
  const router = useRouter();
  const { t } = useTranslation();
  const { colors } = useAppTheme();
  const logout = useAuthStore((state) => state.logout);

  const handleNavigate = (view: string) => {
    router.setParams({ view });
  };

  return (
    <View style={[styles.container, { backgroundColor: unisonColors.bgNlWeak }]}>
      <ScreenAppbar testID="settings-appbar">
        <Appbar.Content title={t('settings.title')} titleStyle={{ color: colors.textNdPrimary }} />
      </ScreenAppbar>
      <ScrollView style={styles.content} contentContainerStyle={styles.contentContainer}>
        <ProfileCard />
        <SettingsMenuList onNavigate={handleNavigate} />
        <SettingsAppInfoSection />
        <View style={styles.spacer} />
        <Text style={[styles.versionLabel, { color: colors.textNdMuted }]}>
          {t('settings.versionLabel', {
            version: Application.nativeApplicationVersion ?? Constants.expoConfig?.version ?? '',
          })}
        </Text>
        <SignOutCard onSignOut={logout} />
      </ScrollView>
    </View>
  );
};

export const Settings = () => {
  const router = useRouter();
  const { view } = useLocalSearchParams<{ view?: string }>();

  const handleBack = () => {
    router.setParams({ view: undefined });
  };

  if (view === NETWORK_VIEW) {
    return (
      <View style={{ flex: 1 }}>
        <NetworkSettings onBack={handleBack} />
      </View>
    );
  }

  if (view === QUOTA_USAGE_VIEW) {
    return (
      <View style={{ flex: 1 }}>
        <QuotaUsageSettings onBack={handleBack} />
      </View>
    );
  }

  if (view === STORAGE_VIEW) {
    return (
      <View style={{ flex: 1 }}>
        <StorageSettings onBack={handleBack} />
      </View>
    );
  }

  return (
    <View style={{ flex: 1 }}>
      <SettingsMenuView />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    flex: 1,
  },
  contentContainer: {
    flexGrow: 1,
    gap: spacing[2],
    paddingHorizontal: spacing[4],
    paddingTop: spacing[4],
    paddingBottom: spacing[8],
  },
  spacer: {
    flex: 1,
  },
  versionLabel: {
    textAlign: 'center',
    marginBottom: spacing[2],
  },
});
